import { Injectable } from '@angular/core';
import { catchError, Observable, of, take } from 'rxjs';
import { ApiResponse } from '../../models/ApiResponse';
import { environment } from '../../../../environments/environment';
import { TicketClosingField } from '../../models/Ticket/TicketClosingField';
import { TicketAttachmentResponse, TicketAttachmentUrlResponse } from '../../models/Ticket/TicketAttachment';
import { TicketComment } from '../../models/Ticket/TicketComment';
import { HttpClient, HttpParams } from '@angular/common/http';
import { PagedTicketResponse } from '../../models/Ticket/PagedTicketResponse';
import { TicketCloseRequest } from '../../models/Ticket/TicketCloseRequest';
import { TicketDetailResponse } from '../../models/Ticket/TaskClosingField';

@Injectable({
  providedIn: 'root',
})
export class TicketService {
  public URL_API = environment.apiUrl;
  constructor(
    private http: HttpClient,
  ) { }

  searchClosedTicketsByOdt(params: { page: number; pageSize: number; searchFieldValue: string }): Observable<ApiResponse<PagedTicketResponse> | null> {
    const httpParams = new HttpParams()
      .set('page', params.page.toString())
      .set('pageSize', params.pageSize.toString())
      .set('searchfield', 'ODT')
      .set('searchFieldValue', params.searchFieldValue);
    return this.http
      .get<ApiResponse<PagedTicketResponse>>(`${this.URL_API}/api/ClosedTicket/Paged`, { params: httpParams })
      .pipe(take(1), catchError(() => of(null)));
  }

  searchOpenTicketsByOdt(params: { page: number; pageSize: number; searchFieldValue: string }): Observable<ApiResponse<PagedTicketResponse> | null> {
    const httpParams = new HttpParams()
      .set('page', params.page.toString())
      .set('pageSize', params.pageSize.toString())
      .set('searchfield', 'ODT')
      .set('searchFieldValue', params.searchFieldValue);
    return this.http
      .get<ApiResponse<PagedTicketResponse>>(`${this.URL_API}/api/OpenTicket/Paged`, { params: httpParams })
      .pipe(take(1), catchError(() => of(null)));
  }

  getClosingFieldsByTicketId(ticketId: number): Observable<ApiResponse<TicketClosingField[]> | null> {
    const params = new HttpParams().set('ticketId', ticketId.toString());
    return this.http
      .get<ApiResponse<TicketClosingField[]>>(`${this.URL_API}/api/TicketClosingField/List`, { params })
      .pipe(
        take(1),
        catchError(() => of(null))
      );
  }

  getAttachment(ticketId: number, attachmentId: number): Observable<ApiResponse<TicketAttachmentResponse> | null> {
    const params = new HttpParams()
      .set('TicketId', ticketId.toString())
      .set('AttachmentId', attachmentId.toString());
    return this.http
      .get<ApiResponse<TicketAttachmentResponse>>(`${this.URL_API}/api/TicketAttachment/GetAttachment`, { params })
      .pipe(
        take(1),
        catchError(() => of(null))
      );
  }

  getAttachmentUrl(ticketId: number, attachmentId: number): Observable<ApiResponse<TicketAttachmentUrlResponse> | null> {
    const params = new HttpParams()
      .set('TicketId', ticketId.toString())
      .set('AttachmentId', attachmentId.toString());
    return this.http
      .get<ApiResponse<TicketAttachmentUrlResponse>>(`${this.URL_API}/api/TicketAttachment/GetAttachmentUrl`, { params })
      .pipe(
        take(1),
        catchError(() => of(null))
      );
  }

  async getFile(url: string, fileName: string, contentType: string, contentMD5: string): Promise<File> {
    const fetchOptions: RequestInit = {
      headers: {
        'Content-Type': contentType,
        'Content-MD5': contentMD5,
      },
    };
    try {
      const response = await fetch(url, fetchOptions);
      const blob = await response.blob();
      return new File([blob], fileName || 'archivo', { type: contentType });
    } catch (error) {
      throw error;
    }
  }

  getTicketComments(ticketId: number): Observable<ApiResponse<TicketComment[]> | null> {
    const params = new HttpParams().set('ticketId', ticketId.toString());
    return this.http
      .get<ApiResponse<TicketComment[]>>(`${this.URL_API}/api/TicketComment/List`, { params })
      .pipe(take(1), catchError(() => of(null)));
  }

  createTicketComment(ticketId: number, comment: string): Observable<ApiResponse<null> | null> {
    const now = new Date();
    const day = String(now.getDate()).padStart(2, '0');
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const year = now.getFullYear();
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    const currentDate = `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;

    return this.http
      .post<ApiResponse<null>>(`${this.URL_API}/api/TicketComment/Create`, {
        CommentTypeId: environment.commentTypeId,
        TicketId: ticketId,
        Comment: comment,
        CurrentDate: currentDate,
      })
      .pipe(take(1), catchError(() => of(null)));
  }

  approveTicket(request: TicketCloseRequest): Observable<ApiResponse<null> | null> {
    return this.http
      .post<ApiResponse<null>>(`${this.URL_API}/api/Ticket/Close`, request)
      .pipe(take(1), catchError(() => of(null)));
  }

  rejectTicket(request: TicketCloseRequest): Observable<ApiResponse<null> | null> {
    return this.http
      .post<ApiResponse<null>>(`${this.URL_API}/api/Ticket/Close`, request)
      .pipe(take(1), catchError(() => of(null)));
  }

  getTicketDetail(ticketId: number): Observable<ApiResponse<TicketDetailResponse> | null> {
    return this.http
      .get<ApiResponse<TicketDetailResponse>>(`${this.URL_API}/api/Ticket/Detail/${ticketId}`)
      .pipe(take(1), catchError(() => of(null)));
  }

}


